import { Align, Arrange, Container, SvgUndo, TextButton } from '@loomhq/lens';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { watchAgain } from 'creators/videoPlayer';

import { selectCtaEnabled } from 'selectors/shareVideo';
import {
  selectCommentsEnabled,
  selectCommentsOrEmojiEnabled,
} from 'selectors/video';
import { selectIsMainVideoOnSharePage } from 'selectors/videoPlayer';

import ReactionCommentNudge from './reaction-comment-nudge';
import CtaButton from '../embed-top-bar/cta-button-rework';

import styles from './styles.module.less';

const PLAYER_WIDTH_THRESHOLD = 550;
const PLAYER_HEIGHT_THRESHOLD = 320;

const DEFAULT_VISIBLE_ELEMENTS = {
  cta: false,
  reactionNudge: false,
  watchAgain: false,
};

// Note: This component does not support emoji reacting for anonymous users at this time.
// Since we're only reworking logged in flows right now, that is fine

const EndScreenContents = props => {
  const { width, height } = props;
  const [visibleElements, setVisibleElements] = useState(
    DEFAULT_VISIBLE_ELEMENTS
  );

  const commentsEnabled = useSelector(selectCommentsEnabled);

  const ctaEnabled = useSelector(selectCtaEnabled);
  const commentsOrEmojiEnabled = useSelector(selectCommentsOrEmojiEnabled);
  const isMainVideoOnSharePage = useSelector(selectIsMainVideoOnSharePage);

  const compact =
    width < PLAYER_WIDTH_THRESHOLD || height < PLAYER_HEIGHT_THRESHOLD;

  useEffect(() => {
    let endScreenElements = [];

    ctaEnabled && endScreenElements.push('cta');

    if (commentsOrEmojiEnabled && isMainVideoOnSharePage) {
      endScreenElements.push('reactionNudge');
    }

    // show watch again, only if we do not have anything else to show
    if (endScreenElements.length === 0) {
      endScreenElements.push('watchAgain');
    }

    // if player size is below threshold, only show one element (CTA if available, nudge section otherwise)
    if (compact) {
      endScreenElements = endScreenElements.splice(0, 1);
    }

    setVisibleElements(
      endScreenElements.reduce(
        (acc, current) => {
          acc[current] = true;

          return acc;
        },
        { ...DEFAULT_VISIBLE_ELEMENTS }
      )
    );
  }, [
    isMainVideoOnSharePage,
    commentsEnabled,
    ctaEnabled,
    commentsOrEmojiEnabled,
    compact,
  ]);

  return (
    <Arrange justifyContent="center" gap="4" autoFlow="row">
      {visibleElements.cta && <CtaSection />}
      {visibleElements.reactionNudge && (
        <ReactionCommentNudge
          fontSize={ctaEnabled ? 'medium' : 'large'}
          width={width}
          height={height}
        />
      )}
      {visibleElements.watchAgain && <WatchAgainButton />}
    </Arrange>
  );
};

const CtaSection = () => {
  return (
    <Container data-testid="cta-section">
      <Align alignment="center">
        <CtaButton size="large" />
      </Align>
    </Container>
  );
};

const WatchAgainButton = () => {
  const dispatch = useDispatch();
  const handleWatchAgain = () => {
    dispatch(watchAgain());
  };

  return (
    <Align alignment="center" data-testid="watch-again-section">
      <Container width="fit-content">
        <TextButton
          onClick={handleWatchAgain}
          altText="Watch Again"
          icon={<SvgUndo />}
        >
          Watch again
        </TextButton>
      </Container>
    </Align>
  );
};

const EndScreen = props => {
  const { EditVideoButtons, ViewerVideoButtons, width, height } = props;

  return (
    <div className={cx(styles.endScreenContainer, 'theme-dark')}>
      <Align alignment="center">
        <EndScreenContents width={width} height={height} />
      </Align>

      {/*  copied from old end screen: */}
      {EditVideoButtons && (
        <div className="edit-video-buttons absolute right:0 text:right">
          <EditVideoButtons startCollapsed />
        </div>
      )}
      {ViewerVideoButtons && (
        <div
          className={`${'viewer-video-buttons'} absolute right:0 text:right`}
        >
          <ViewerVideoButtons startCollapsed />
        </div>
      )}
    </div>
  );
};

export default EndScreen;
